/**
 * Filters Component
 *
 * Advanced filter panel for instructor directory search
 * Includes location, materials, providers, fire experience, and verified filters
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { LocationSelector } from '../common/LocationSelector';
import { getAvailableFilters, type AvailableFilters, type SearchFilters } from '../../services/instructorService';

interface FiltersProps {
  filters: SearchFilters;
  onFilterChange: (key: keyof SearchFilters, value: any) => void;
  onClearFilters: () => void;
  resultCount: number;
  className?: string;
}

export function Filters({
  filters,
  onFilterChange,
  onClearFilters,
  resultCount,
  className = ''
}: FiltersProps) {
  const [availableFilters, setAvailableFilters] = useState<AvailableFilters | null>(null);
  const [loadingFilters, setLoadingFilters] = useState(true);

  // Load available filter options from API
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const data = await getAvailableFilters();
        setAvailableFilters(data);
      } catch (err) {
        console.error('[Filters] Error loading filter options:', err);
      } finally {
        setLoadingFilters(false);
      }
    };

    loadFilters();
  }, []);

  // Handle checkbox changes for arrays (materials, providers)
  const handleCheckboxChange = (filterKey: 'materials' | 'providers', value: string, checked: boolean) => {
    const currentValues = (filters[filterKey] as string[]) || [];

    let newValues: string[];
    if (checked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter((v) => v !== value);
    }

    onFilterChange(filterKey, newValues.length > 0 ? newValues : undefined);
  };

  // Check if a checkbox is checked
  const isChecked = (filterKey: 'materials' | 'providers', value: string): boolean => {
    const currentValues = (filters[filterKey] as string[]) || [];
    return currentValues.includes(value);
  };

  // Count active filters
  const activeFilterCount = Object.keys(filters).filter(key => {
    const value = filters[key as keyof SearchFilters];
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null && value !== '';
  }).length;

  return (
    <div class={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      {/* Header */}
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-xl font-bold text-gray-900">
          Filtros
          {activeFilterCount > 0 && (
            <span class="ml-2 text-sm font-normal text-gray-600">
              ({activeFilterCount} activo{activeFilterCount !== 1 ? 's' : ''})
            </span>
          )}
        </h2>

        {/* Clear Filters Button */}
        {activeFilterCount > 0 && (
          <button
            onClick={onClearFilters}
            class="text-sm text-blue-600 hover:text-blue-800 font-medium transition"
          >
            Limpiar todo
          </button>
        )}
      </div>

      {/* Result Count */}
      <div class="mb-6 p-3 bg-blue-50 rounded-lg">
        <p class="text-sm text-blue-900">
          <span class="font-semibold">{resultCount}</span> instructor{resultCount !== 1 ? 'es' : ''} encontrado{resultCount !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Location Filters */}
      <div class="mb-6 pb-6 border-b border-gray-200">
        <h3 class="text-sm font-semibold text-gray-900 mb-3">Ubicación</h3>
        <LocationSelector
          countryValue={filters.country || ''}
          stateValue={filters.state || ''}
          cityValue={filters.city || ''}
          onCountryChange={(name) => onFilterChange('country', name || undefined)}
          onStateChange={(name) => onFilterChange('state', name || undefined)}
          onCityChange={(name) => onFilterChange('city', name || undefined)}
          layout="column"
          required={{ country: false, state: false, city: false }}
        />
      </div>

      {/* Fire Experience Filter */}
      {!loadingFilters && availableFilters && (
        <div class="mb-6 pb-6 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-900 mb-3">Experiencia con Fuego</h3>
          <select
            value={filters.nivel_de_experiencia_en_fuego || ''}
            onChange={(e) => onFilterChange('nivel_de_experiencia_en_fuego', e.currentTarget.value || undefined)}
            class="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Todos los niveles</option>
            {availableFilters.nivel_de_experiencia_en_fuego.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Materials Filter */}
      {!loadingFilters && availableFilters && (
        <div class="mb-6 pb-6 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-900 mb-3">Material DAI</h3>
          <div class="space-y-2">
            {availableFilters.materials.map((option) => (
              <label key={option.value} class="flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  checked={isChecked('materials', option.value)}
                  onChange={(e) => handleCheckboxChange('materials', option.value, e.currentTarget.checked)}
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span class="ml-3 text-sm text-gray-700 group-hover:text-gray-900">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Providers Filter */}
      {!loadingFilters && availableFilters && (
        <div class="mb-6 pb-6 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-900 mb-3">Proveedores</h3>
          <div class="space-y-2">
            {availableFilters.providers.map((option) => (
              <label key={option.value} class="flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  checked={isChecked('providers', option.value)}
                  onChange={(e) => handleCheckboxChange('providers', option.value, e.currentTarget.checked)}
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span class="ml-3 text-sm text-gray-700 group-hover:text-gray-900">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Verified Filter */}
      <div class="mb-0">
        <label class="flex items-center cursor-pointer group">
          <input
            type="checkbox"
            checked={filters.verified === true}
            onChange={(e) => onFilterChange('verified', e.currentTarget.checked || undefined)}
            class="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-2 focus:ring-green-500"
          />
          <span class="ml-3 text-sm font-medium text-gray-900 group-hover:text-gray-700">
            Solo instructores verificados
          </span>
          <svg
            class="ml-1 w-4 h-4 text-green-500"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
        </label>
      </div>

      {/* Loading State */}
      {loadingFilters && (
        <div class="mt-6 text-center text-sm text-gray-500">
          Cargando opciones de filtros...
        </div>
      )}
    </div>
  );
}
